import React from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Dumbbell,
  RefreshCw,
  Apple,
  Shield,
  Sun,
  Clock,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import Header from '../components/Header';
import FooterSection from '../components/home/FooterSection';
import WhatsAppFloat from '../components/home/WhatsAppFloat';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';

const CAPABILITY_DOMAINS = [
  {
    icon: Activity,
    title: 'Mobility',
    description:
      'Joint range, movement quality and functional mobility across the body.',
  },
  {
    icon: Dumbbell,
    title: 'Strength',
    description:
      'Force production, muscular capacity and progressive load tolerance.',
  },
  {
    icon: RefreshCw,
    title: 'Recovery',
    description:
      'Sleep quality, parasympathetic balance and adaptation capacity.',
  },
  {
    icon: Apple,
    title: 'Nutrition',
    description:
      'Metabolic health markers, fueling strategy and nutrient intake.',
  },
  {
    icon: Shield,
    title: 'Resilience',
    description:
      'Stress tolerance, psychological flexibility and chronic-load management.',
  },
  {
    icon: Sun,
    title: 'Lifestyle',
    description:
      'Daily movement patterns, posture, behaviour and environmental factors.',
  },
];

const WHY_IT_MATTERS = [
  {
    title: 'Identify Your Capability Baseline',
    description:
      'Understand where your strength, mobility and resilience stand today across six measurable domains.',
  },
  {
    title: 'Personalised Program Direction',
    description:
      'Get matched to the Zeeva program — Reset, Strength, Mobility, Recovery or Active Aging — that closes your gaps fastest.',
  },
  {
    title: 'Evidence-Based, Not Guesswork',
    description:
      'Rooted in current exercise science, rehabilitation research and longevity literature.',
  },
];

const COMING_SOON_FEATURES = [
  'Mobility & range-of-motion screen',
  'Strength benchmarks (relative & absolute)',
  'Recovery quality index',
  'Nutritional self-audit',
  'Resilience & stress profile',
  'Lifestyle & movement habits inventory',
];

const scoreRanges = [
  { range: '0–40', label: 'Foundational', color: 'rgba(239, 68, 68, 0.85)' },
  { range: '41–70', label: 'Developing', color: 'rgba(245, 158, 11, 0.9)' },
  { range: '71–100', label: 'Resilient', color: 'rgba(29, 185, 84, 0.95)' },
];

const Assessment = () => {
  return (
    <div className="min-h-screen" data-testid="assessment-page">
      <Seo
        title="The Zeeva Capability Assessment"
        description="Discover your capability baseline across Mobility, Strength, Recovery, Nutrition, Resilience and Lifestyle. Get a personalised Zeeva program recommendation in 12–15 minutes."
        path="/assessment"
      />
      <Header />

      <main style={{ paddingTop: '110px' }}>
        {/* Hero / Introduction */}
        <section
          className="hero-section"
          style={{ minHeight: 'auto', paddingTop: '3rem', paddingBottom: '3rem' }}
          data-testid="assessment-hero"
        >
          <div className="container">
            <div className="hero-content" style={{ maxWidth: '780px' }}>
              <p
                style={{
                  fontSize: '0.85rem',
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  color: 'var(--brand-accent, #FFD700)',
                  fontWeight: 700,
                  marginBottom: '1rem',
                }}
                data-testid="assessment-eyebrow"
              >
                The Zeeva Capability Assessment
              </p>
              <h1
                className="hero-title"
                style={{ fontSize: 'clamp(2.25rem, 4.5vw, 3.5rem)' }}
                data-testid="assessment-title"
              >
                Discover Your Capability Baseline
              </h1>
              <p className="hero-subtitle" data-testid="assessment-subtitle">
                A structured, evidence-based assessment across six capability
                domains — Mobility, Strength, Recovery, Nutrition, Resilience and
                Lifestyle — to map exactly where you are and how to move forward.
              </p>

              <div className="flex flex-wrap items-center gap-4 mt-6">
                <Link to="/assessment/start" data-testid="assessment-hero-start-btn">
                  <Button className="btn-cta" style={{ fontSize: '1rem' }}>
                    Begin Assessment Now
                    <ArrowRight className="ml-2" size={20} aria-hidden="true" />
                  </Button>
                </Link>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'var(--text-light, rgba(255,255,255,0.75))',
                  }}
                >
                  <Clock size={18} aria-hidden="true" />
                  <span
                    style={{ fontSize: '0.95rem' }}
                    data-testid="assessment-time-estimate"
                  >
                    12–15 minutes
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Assessment Matters */}
        <section
          className="section-padding bg-subtle"
          data-testid="assessment-why-matters"
        >
          <div className="container">
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <h2 className="section-title">Why a Capability Assessment Matters</h2>
              <p className="section-subtitle">
                You can't change what you don't measure. The assessment establishes
                a clear baseline — the foundation for everything that follows.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {WHY_IT_MATTERS.map((item) => (
                <div
                  key={`why-${item.title}`}
                  className="reason-card"
                  data-testid={`why-card-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div
                    className="service-icon"
                    style={{ marginBottom: '1rem' }}
                    aria-hidden="true"
                  >
                    <CheckCircle2 size={28} />
                  </div>
                  <h3 className="reason-title">{item.title}</h3>
                  <p className="reason-description">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What Will Be Measured */}
        <section
          className="section-padding"
          data-testid="assessment-domains"
        >
          <div className="container">
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <h2 className="section-title">What Will Be Measured</h2>
              <p className="section-subtitle">
                Six capability domains, each scored independently and combined into
                your Zeeva Capability Score.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {CAPABILITY_DOMAINS.map((domain) => {
                const Icon = domain.icon;
                return (
                  <div
                    key={`domain-${domain.title}`}
                    className="reason-card"
                    data-testid={`assessment-domain-${domain.title.toLowerCase()}`}
                  >
                    <div
                      className="service-icon"
                      style={{ marginBottom: '1rem' }}
                      aria-hidden="true"
                    >
                      <Icon size={28} />
                    </div>
                    <h3 className="reason-title">{domain.title}</h3>
                    <p className="reason-description">{domain.description}</p>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                marginTop: '3rem',
                maxWidth: '720px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <h3
                style={{
                  textAlign: 'center',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  marginBottom: '0.5rem',
                }}
              >
                Inside Each Module
              </h3>
              <p
                style={{
                  textAlign: 'center',
                  color: 'var(--text-light, rgba(255,255,255,0.7))',
                  fontSize: '0.9rem',
                  marginBottom: '1.5rem',
                }}
              >
                Each domain is scored from your self-report responses. The full
                assessment combines movement screens (clinical), lifestyle
                inventories and capability benchmarks.
              </p>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '0.75rem',
                }}
                data-testid="assessment-modules-list"
              >
                {COMING_SOON_FEATURES.map((feature) => (
                  <li
                    key={`feature-${feature}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      fontSize: '0.92rem',
                    }}
                  >
                    <CheckCircle2
                      size={16}
                      className="text-brand-primary flex-shrink-0"
                      aria-hidden="true"
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Capability Score Architecture */}
        <section
          className="section-padding bg-section"
          data-testid="assessment-score-section"
        >
          <div className="container">
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <h2 className="section-title">The Zeeva Capability Score</h2>
              <p className="section-subtitle">
                A composite score (0–100) representing your current capability
                across the six domains. Scoring calculations are part of the next
                development phase.
              </p>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '1rem',
                maxWidth: '900px',
                margin: '0 auto',
              }}
              data-testid="assessment-score-ranges"
            >
              {scoreRanges.map((band) => (
                <div
                  key={`score-${band.label}`}
                  className="reason-card"
                  style={{
                    textAlign: 'center',
                    borderTop: `4px solid ${band.color}`,
                  }}
                  data-testid={`score-band-${band.label.toLowerCase()}`}
                >
                  <p
                    style={{
                      fontSize: '2rem',
                      fontWeight: 800,
                      color: band.color,
                      margin: '0 0 0.25rem',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {band.range}
                  </p>
                  <p
                    style={{
                      fontSize: '0.95rem',
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      color: 'var(--text-secondary, rgba(255,255,255,0.85))',
                      fontWeight: 700,
                      margin: 0,
                    }}
                  >
                    {band.label}
                  </p>
                </div>
              ))}
            </div>

            <p
              style={{
                textAlign: 'center',
                marginTop: '2rem',
                color: 'var(--text-light, rgba(255,255,255,0.7))',
                fontStyle: 'italic',
                fontSize: '0.9rem',
              }}
            >
              The score is paired with a personalised recommendation that routes
              you to the most relevant Zeeva program.
            </p>
          </div>
        </section>

        {/* Call To Begin */}
        <section
          className="cta-section"
          data-testid="assessment-call-to-begin"
        >
          <div className="container">
            <div className="cta-content">
              <h2 className="cta-title">Ready to Begin?</h2>
              <p className="cta-subtitle">
                The Capability Assessment takes 12–15 minutes. Six domains,
                twenty-four questions, one clear recommendation.
              </p>
              <div className="cta-buttons">
                <Link to="/assessment/start" data-testid="assessment-begin-btn">
                  <Button className="btn-cta">
                    Begin Assessment
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/#programs" data-testid="assessment-begin-programs">
                  <Button className="btn-secondary">Explore Programs</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <FooterSection onScrollToSection={() => {}} />
      <WhatsAppFloat />
    </div>
  );
};

export default Assessment;
